import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { useTheme } from '../../../context/ThemeContext';
import {
  Upload,
  AlertCircle,
  Send,
  MapPin,
  TrendingUp,
  CheckCircle,
  Wifi,
  Trash2,
  Power,
  Gauge,
  Building2
} from 'lucide-react';
import Field from '../../shared/components/Field';
import ClearDataModal from './ClearDataModal';
import ContractClausesSettings from './ContractClausesSettings';
import { writeSettingsCache } from '../../../services/settingsService';
import { supabase, uploadToStorage, getAuthToken } from '../../../config/supabase';
import { traccarLogin, traccarGetDevices, traccarGetPositions } from '../../gps/utils/traccar';
import { afoulkiFetchAll } from '../../gps/utils/afoulki';
import { trackyFetchAll } from '../../gps/utils/tracky';
import { iopgpsFetchAll } from '../../gps/utils/iopgps';
import { useAuth } from '../../../context/AuthContext';
import {
  pushSupported, getPermissionStatus, requestPermissionAndToken, revokeToken,
} from '../../../firebase/fcmService';

export default function SettingsPage({ settings, setSettings }) {
  const { t, lang } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  
  const [local, setLocal] = useState(settings);
  const [saved, setSaved] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testingAfoulki, setTestingAfoulki] = useState(false);
  const [afoulkiTestMsg, setAfoulkiTestMsg] = useState(null);
  const [testingIopgps, setTestingIopgps] = useState(false);
  const [iopgpsTestMsg, setIopgpsTestMsg] = useState(null);
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [showClearData, setShowClearData] = useState(false);

  // ── Firebase Push (per-device enable, shown when channel is push/both) ──
  const [pushSupp, setPushSupp] = useState(null);
  const [pushPerm, setPushPerm] = useState(getPermissionStatus());
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState('');
  useEffect(() => {
    let alive = true;
    (async () => { const ok = await pushSupported(); if (alive) setPushSupp(ok); })();
    return () => { alive = false; };
  }, []);
  const flashPush = (m) => { setPushMsg(m); setTimeout(() => setPushMsg(''), 4000); };
  const enablePush = async () => {
    setPushBusy(true);
    try {
      await requestPermissionAndToken();
      setPushPerm(getPermissionStatus());
      flashPush(lang === 'ar' ? '✅ تم تفعيل الإشعارات على هذا الجهاز.' : '✅ Notifications activées sur cet appareil.');
    } catch (e) { flashPush('❌ ' + e.message); }
    setPushBusy(false);
  };
  const disablePush = async () => {
    setPushBusy(true);
    await revokeToken();
    setPushPerm(getPermissionStatus());
    flashPush(lang === 'ar' ? '🔕 تم تعطيل الإشعارات على هذا الجهاز.' : '🔕 Notifications désactivées sur cet appareil.');
    setPushBusy(false);
  };

  const save = async () => {
    writeSettingsCache(local, window.__AGENCY_ID__);
    setSettings(local);

    // Sync to Supabase database
    const userId = user?.id || user?.uid;
    if (userId) {
      try {
        const { data: reg } = await supabase
          .from('user_registrations')
          .select('data')
          .eq('uid', userId)
          .single();

        const currentData = reg?.data || {};
        const mergedData = { ...currentData, ...local };

        await supabase
          .from('user_registrations')
          .update({ data: mergedData })
          .eq('uid', userId);
      } catch (err) {
        console.error('Failed to sync settings to Supabase:', err.message);
      }
    }

    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const uploadLogo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    uploadToStorage(file, 'settings')
      .then(url => setLocal(l => ({ ...l, logo: url })))
      .catch(err => alert('❌ ' + err.message));
  };

  // OPEN ACCESS: every agency manages its own logo, cachet and signature, plus
  // the auto-insert toggle (no longer Super-Admin-only).
  const isSA = true;

  // Import the stamp / signature INSTANTLY as a base64 data URL (no upload, no
  // network wait). Storing it inline also makes invoice rendering immune to
  // browser/URL caching, CORS and private-bucket signed-URL problems.
  const uploadStampImage = (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!/^image\//.test(file.type || '')) {
      alert(lang === 'ar' ? 'الملف يجب أن يكون صورة' : 'Le fichier doit être une image');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert(lang === 'ar'
        ? 'الصورة كبيرة جداً (2MB كحد أقصى)'
        : 'Image trop grande (2MB max)');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setLocal(l => ({ ...l, [fieldName]: reader.result })); // instant preview + persisted on save
    reader.onerror = () => alert(lang === 'ar' ? 'تعذّر قراءة الصورة' : 'Impossible de lire l\'image');
    reader.readAsDataURL(file);
  };
  const uploadStamp = (e) => uploadStampImage(e, 'companyStamp');
  const uploadSignature = (e) => uploadStampImage(e, 'companySignature');

  // Toggle the automatic insertion; stamp the enable time so only invoices for
  // rentals created afterwards receive the auto signature/stamp.
  const toggleAutoStamp = () => {
    setLocal(l => {
      const on = !(l.autoStampSignature === true || l.autoStampSignature === 'true');
      return { ...l, autoStampSignature: on, autoStampSince: on ? (l.autoStampSince || new Date().toISOString()) : l.autoStampSince };
    });
  };

  const handleTestTelegram = async () => {
    if (!local.telegramChatId) {
      alert(lang === 'ar' ? 'دخّل Chat ID الأول' : 'Entrez d\'abord votre Chat ID'); 
      return;
    }
    setTestingTelegram(true);
    try {
      const company = local.companyName || 'CAR TRACK APP';
      const tgLang = local.telegramLang || 'fr';
      const testText = tgLang === 'ar'
        ? `✅ رسالة اختبار من ${company} — إشعارات Telegram تعمل! 🎉`
        : tgLang === 'en'
        ? `✅ Test message from ${company} — Telegram notifications are working! 🎉`
        : `✅ Message de test depuis ${company} — les notifications Telegram fonctionnent ! 🎉`;
      const authToken = await getAuthToken();
      const res = await fetch('/api/telegram/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {})
        },
        body: JSON.stringify({
          chatId: local.telegramChatId,
          text: testText
        })
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        alert(lang === 'ar' ? 'تمام! وصل الاختبار.' : 'Parfait! Test reçu.');
      } else {
        alert((lang === 'ar' ? 'مشكل: ' : 'Problème: ') + (data.error || 'vérifiez le Chat ID et que vous avez fait START sur @track_carbot'));
      }
    } catch (err) { 
      alert('Erreur: ' + err.message); 
    } finally {
      setTestingTelegram(false);
    }
  };

  // ── Notification Preferences Helpers ─────────────────────────────────────
  const NOTIFICATION_KEYS = [
    'new_rental', 'rental_completed',
    'new_client', 'client_payment', 'client_history_payment',
    'new_reservation', 'online_reservation',
    'new_car', 'engine_control',
    'vidange_done', 'maint_warning', 'maint_critical', 'maint_overdue',
    'new_staff', 'staff_payment',
    'new_cheque', 'cheque_status',
    'contravention', 'contract_signed',
    'treatment_unpaid', 'treatment_paid'
  ];

  const getNotifEnabled = (type) => local.telegramNotifications?.[type] !== false;

  const setNotifEnabled = (type, enabled) => {
    setLocal(prev => ({
      ...prev,
      telegramNotifications: { ...prev.telegramNotifications, [type]: enabled }
    }));
  };

  const enableAllNotifications = () => {
    const all = {};
    NOTIFICATION_KEYS.forEach(k => { all[k] = true; });
    setLocal(prev => ({ ...prev, telegramNotifications: all }));
  };

  const disableAllNotifications = () => {
    const all = {};
    NOTIFICATION_KEYS.forEach(k => { all[k] = false; });
    setLocal(prev => ({ ...prev, telegramNotifications: all }));
  };

  // Master switch — backward compatible (undefined/true = enabled)
  const telegramEnabled = local.telegramEnabled !== false;

  const NOTIFICATION_CATEGORIES = [
    {
      emoji: '📑',
      label_fr: 'Contrats & Locations',
      label_ar: 'العقود والإيجارات',
      color: 'blue',
      types: [
        { key: 'new_rental', fr: 'Nouvelle location', ar: 'إيجار جديد', desc_fr: 'Quand un contrat est créé', desc_ar: 'عند إنشاء عقد إيجار' },
        { key: 'rental_completed', fr: 'Location terminée', ar: 'إيجار منتهٍ', desc_fr: 'Quand une voiture est retournée', desc_ar: 'عند إرجاع السيارة' },
        { key: 'new_reservation', fr: 'Réservation confirmée', ar: 'حجز مؤكد', desc_fr: 'Quand une réservation est confirmée', desc_ar: 'عند تأكيد حجز' },
        { key: 'online_reservation', fr: 'Réservation en ligne', ar: 'حجز عبر الإنترنت', desc_fr: 'Demande de réservation publique', desc_ar: 'طلب حجز عبر الرابط العام' },
        { key: 'contract_signed', fr: 'Contrat signé', ar: 'عقد موقع', desc_fr: 'Quand un client signe en ligne', desc_ar: 'عند توقيع العميل إلكترونياً' },
      ]
    },
    {
      emoji: '🚙',
      label_fr: 'Alertes Véhicules',
      label_ar: 'تنبيهات المركبات',
      color: 'orange',
      types: [
        { key: 'new_car', fr: 'Nouvelle voiture', ar: 'سيارة جديدة', desc_fr: 'Quand une voiture est ajoutée', desc_ar: 'عند إضافة سيارة جديدة' },
        { key: 'engine_control', fr: 'Contrôle moteur GPS', ar: 'التحكم في المحرك', desc_fr: 'Coupure/activation pompe à essence', desc_ar: 'قطع/تشغيل مضخة البنزين' },
        { key: 'ignition', fr: 'Moteur démarré / arrêté', ar: 'تشغيل / إيقاف المحرك', desc_fr: 'Alerte quand le moteur (contact) démarre ou s\'arrête', desc_ar: 'تنبيه عند تشغيل أو إيقاف المحرك' },
        { key: 'breakdown', fr: 'Panne / Réparation', ar: 'عطل / إصلاح', desc_fr: 'Alerte quand un véhicule est signalé en panne', desc_ar: 'تنبيه عند الإبلاغ عن عطل في مركبة' },
        { key: 'hood_opened', fr: 'Capot ouvert (Capot)', ar: 'فتح الكابو', desc_fr: 'Alerte quand le capot (DI1/DI2) est ouvert', desc_ar: 'تنبيه عند فتح غطاء المحرك (DI1/DI2)' },
      ]
    },
    {
      emoji: '🔧',
      label_fr: 'Maintenance & Entretien',
      label_ar: 'الصيانة والخدمة',
      color: 'yellow',
      types: [
        { key: 'vidange_done', fr: 'Vidange effectuée', ar: 'تغيير الزيت', desc_fr: 'Quand une vidange est enregistrée', desc_ar: 'عند تسجيل تغيير الزيت' },
        { key: 'maint_warning', fr: 'Alerte Proche (80%)', ar: 'تنبيه اقتراب الصيانة (80%)', desc_fr: 'Quand un entretien atteint le seuil d\'avertissement', desc_ar: 'عندما تقترب الصيانة من الحد المحدد' },
        { key: 'maint_critical', fr: 'Alerte Critique (90%)', ar: 'تنبيه حرج (90%)', desc_fr: 'Quand un entretien atteint le seuil critique', desc_ar: 'عندما تصبح الصيانة حرجة' },
        { key: 'maint_overdue', fr: 'Alerte Dépassée (100% / En retard)', ar: 'تنبيه فوات الأوان (100%)', desc_fr: 'Quand l\'entretien a dépassé son échéance', desc_ar: 'عندما يتجاوز الكيلومترات المحددة للخدمة' },
      ]
    },
    {
      emoji: '⚙️',
      label_fr: 'Notifications Système',
      label_ar: 'إشعارات النظام',
      color: 'indigo',
      types: [
        { key: 'new_client', fr: 'Nouveau client', ar: 'عميل جديد', desc_fr: 'Quand une fiche client est créée', desc_ar: 'عند إنشاء بطاقة عميل' },
        { key: 'client_payment', fr: 'Paiement client', ar: 'دفع العميل', desc_fr: 'Paiement depuis la fiche client', desc_ar: 'دفع من بطاقة العميل' },
        { key: 'client_history_payment', fr: 'Paiement historique', ar: 'دفع من السجل', desc_fr: 'Paiement depuis l\'historique client', desc_ar: 'دفع من سجل العميل' },
        { key: 'new_staff', fr: 'Nouvel employé', ar: 'موظف جديد', desc_fr: 'Quand un employé est ajouté', desc_ar: 'عند إضافة موظف جديد' },
        { key: 'staff_payment', fr: 'Paiement employé', ar: 'دفع الموظف', desc_fr: 'Salaire ou avance versée', desc_ar: 'دفع راتب أو سلفة' },
        { key: 'new_cheque', fr: 'Nouveau chèque', ar: 'شيك جديد', desc_fr: 'Quand un chèque est enregistré', desc_ar: 'عند تسجيل شيك جديد' },
        { key: 'cheque_status', fr: 'Statut chèque', ar: 'حالة الشيك', desc_fr: 'Encaissé, rejeté ou annulé', desc_ar: 'صرف، رفض أو إلغاء شيك' },
        { key: 'contravention', fr: 'Contravention NARSA', ar: 'مخالفة نارسا', desc_fr: 'Quand une contravention est créée', desc_ar: 'عند تسجيل مخالفة' },
        { key: 'treatment_unpaid', fr: 'Traitement impayé / en retard', ar: 'معالجة غير مدفوعة / متأخرة', desc_fr: 'Quand un traitement est impayé ou en retard', desc_ar: 'عند وجود معالجة غير مدفوعة أو متأخرة' },
        { key: 'treatment_paid', fr: 'Traitement payé', ar: 'معالجة مدفوعة', desc_fr: 'Quand un traitement est réglé', desc_ar: 'عند تسوية معالجة' },
      ]
    },
  ];

  const handleTestTraccar = async () => {
    if (!local.traccarUrl || !local.traccarEmail || !local.traccarPassword) {
      alert(lang === 'ar' ? 'يرجى ملء جميع الحقول' : 'Veuillez remplir tous les champs');
      return;
    }
    // Tracky uses its own JSON/JWT adapter (not GPSWOX).
    if (local.traccarProvider === 'tracky' || local.gpsProvider === 'tracky') {
      setTestingConnection(true);
      try {
        const res = await trackyFetchAll({
          gpsProvider: 'tracky',
          traccarUrl: local.traccarUrl,
          traccarEmail: local.traccarEmail,
          traccarPassword: local.traccarPassword,
        });
        if (res.success) {
          alert((lang === 'ar' ? '✅ Tracky متصل! عدد المركبات: ' : '✅ Tracky connecté ! Véhicules: ') + res.data.length);
        } else {
          alert('❌ Tracky: ' + (res.error || 'échec') + (res.details ? `\n${res.details}` : ''));
        }
      } catch (e) {
        alert('❌ Tracky: ' + e.message);
      }
      setTestingConnection(false);
      return;
    }
    setTestingConnection(true);
    try {
      const login = await traccarLogin(local.traccarUrl, local.traccarEmail, local.traccarPassword);
      
      if (!login.success) {
        if (login.errorCode === 'ALLOWLIST') {
          const msg = lang === 'ar'
            ? `❌ مشكلة Allowlist في خادم Traccar

السبب: عنوان IP الخاص بك ليس في قائمة المسموح بها.

📋 رسالة الخادم: "${login.error}"

🛠️ الحل (يجب على مدير الخادم):

1️⃣ افتح ملف الإعدادات:
   /opt/traccar/conf/traccar.xml

2️⃣ أضف أو عدّل:
   <entry key='filter.client.allowlist'></entry>
   (فارغ = السماح للجميع)
   
   أو حدد IPs المسموحة:
   <entry key='filter.client.allowlist'>YOUR_IP,IP2</entry>

3️⃣ أعد تشغيل خادم Traccar:
   sudo systemctl restart traccar

⚠️ بدون ذلك، حتى مع كلمة سر صحيحة، الخادم يرفض الاتصال قبل التحقق من الهوية.`
            : `❌ Erreur Allowlist sur le serveur Traccar

Cause: Votre adresse IP n'est pas autorisée par le serveur.

📋 Message du serveur: "${login.error}"

🛠️ Solution (l'admin du serveur doit faire):

1️⃣ Ouvrir le fichier de config:
   /opt/traccar/conf/traccar.xml

2️⃣ Ajouter ou modifier:
   <entry key='filter.client.allowlist'></entry>
   (vide = autoriser tous)
   
   Ou spécifier des IPs autorisées:
   <entry key='filter.client.allowlist'>VOTRE_IP,IP2</entry>

3️⃣ Redémarrer Traccar:
   sudo systemctl restart traccar

⚠️ Sans cela, même avec le bon mot de passe, le serveur refuse la connexion AVANT de vérifier l'authentification.

💡 Astuce: Pour trouver votre IP publique, allez sur https://www.whatismyip.com`;
          alert(msg);
          return;
        }
        
        if (login.errorCode === 'CORS') {
          const corsMsg = lang === 'ar'
            ? `❌ خطأ CORS

السبب: خادم Traccar لا يسمح بالاتصال من المتصفح.

🛠️ الحل (مدير الخادم):

1️⃣ في /opt/traccar/conf/traccar.xml أضف:
   <entry key='web.origin'>*</entry>

2️⃣ أعد تشغيل Traccar`
            : `❌ Erreur CORS

Cause: Le serveur Traccar n'autorise pas les requêtes du navigateur.

🛠️ Solution (admin du serveur):

1️⃣ Dans /opt/traccar/conf/traccar.xml ajouter:
   <entry key='web.origin'>*</entry>

2️⃣ Redémarrer Traccar`;
          alert(corsMsg);
          return;
        }
        
        if (login.errorCode === 'AUTH') {
          alert(lang === 'ar' 
            ? '❌ البريد الإلكتروني أو كلمة السر غير صحيحة'
            : '❌ Email ou mot de passe incorrect');
          return;
        }
        
        alert((lang === 'ar' ? '❌ فشل: ' : '❌ Échec: ') + login.error + 
          (login.details ? '\n\n' + login.details : ''));
        return;
      }
      
      const devices = await traccarGetDevices(local.traccarUrl, local.traccarEmail, local.traccarPassword);
      const positions = await traccarGetPositions(local.traccarUrl, local.traccarEmail, local.traccarPassword);
      
      const devicePreview = devices.slice(0, 5).map(d => 
        `  • ${d.name} (ID: ${d.uniqueId || d.id}) - ${d.status || 'unknown'}`
      ).join('\n');
      const moreDevices = devices.length > 5 ? `\n  ... +${devices.length - 5} autres` : '';
      
      const summary = lang === 'ar'
        ? `✓ تم الاتصال بنجاح!

👤 المستخدم: ${login.user?.name || login.user?.email || local.traccarEmail}
📡 الأجهزة: ${devices.length}
📍 المواقع: ${positions.length}

الأجهزة:
${devicePreview || '  لا توجد أجهزة'}${moreDevices}

💡 اذهب إلى صفحة "السيارات" وأضف معرف الجهاز (Unique ID) لكل سيارة.`
        : `✓ Connexion réussie!

👤 Utilisateur: ${login.user?.name || login.user?.email || local.traccarEmail}
📡 Appareils: ${devices.length}
📍 Positions: ${positions.length}

Appareils:
${devicePreview || '  Aucun appareil'}${moreDevices}

💡 Allez dans "Voitures" et ajoutez le Unique ID de chaque appareil à la voiture correspondante.`;
      alert(summary);
    } catch (err) {
      alert((lang === 'ar' ? '❌ خطأ: ' : '❌ Erreur: ') + err.message);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleTestAfoulki = async () => {
    // The URL field shows a default placeholder value even when local.afoulkiUrl
    // is still empty, so default it here and only require email + password.
    const afoulkiUrl = (local.afoulkiUrl || 'https://afoulki.gps1.ma').trim();
    const afoulkiEmail = (local.afoulkiEmail || '').trim();
    const afoulkiPassword = local.afoulkiPassword || '';
    if (!afoulkiEmail || !afoulkiPassword) {
      setAfoulkiTestMsg({ ok: false, text: lang === 'ar' ? 'أدخل البريد/المستخدم وكلمة السر' : 'Saisissez l’utilisateur et le mot de passe' });
      return;
    }
    setTestingAfoulki(true);
    setAfoulkiTestMsg(null);
    try {
      const res = await afoulkiFetchAll({
        afoulkiUrl,
        afoulkiEmail,
        afoulkiPassword,
        corsProxy: local.corsProxy
      });
      if (res && res.success) {
        const n = (res.data || []).length;
        setAfoulkiTestMsg({ ok: true, text: lang === 'ar' ? `✅ تم الاتصال — ${n} سيارة/جهاز` : `✅ Connecté — ${n} véhicule(s)/appareil(s)` });
      } else {
        setAfoulkiTestMsg({ ok: false, text: (lang === 'ar' ? '❌ ' : '❌ ') + (res?.error || (lang === 'ar' ? 'فشل الاتصال' : 'Échec de connexion')) });
      }
    } catch (err) {
      setAfoulkiTestMsg({ ok: false, text: '❌ ' + (err.message || 'Erreur') });
    } finally {
      setTestingAfoulki(false);
    }
  };

  const handleTestIopgps = async () => {
    const iopgpsUrl = (local.iopgpsUrl || 'https://www.iopgps.com').trim();
    const iopgpsEmail = (local.iopgpsEmail || '').trim();
    const iopgpsPassword = local.iopgpsPassword || '';
    if (!iopgpsEmail || !iopgpsPassword) {
      setIopgpsTestMsg({ ok: false, text: lang === 'ar' ? 'أدخل اسم المستخدم/البريد وكلمة السر' : 'Saisissez l’utilisateur/email et le mot de passe' });
      return;
    }
    setTestingIopgps(true);
    setIopgpsTestMsg(null);
    try {
      const res = await iopgpsFetchAll({
        iopgpsUrl,
        iopgpsEmail,
        iopgpsPassword,
        corsProxy: local.corsProxy
      });
      if (res && res.success) {
        const n = (res.data || []).length;
        setIopgpsTestMsg({ ok: true, text: lang === 'ar' ? `✅ تم الاتصال — ${n} سيارة/جهاز` : `✅ Connecté — ${n} véhicule(s)/appareil(s)` });
      } else {
        setIopgpsTestMsg({ ok: false, text: (lang === 'ar' ? '❌ ' : '❌ ') + (res?.error || (lang === 'ar' ? 'فشل الاتصال' : 'Échec de connexion')) });
      }
    } catch (err) {
      setIopgpsTestMsg({ ok: false, text: '❌ ' + (err.message || 'Erreur') });
    } finally {
      setTestingIopgps(false);
    }
  };

  const handleToggleTheme = () => {
    const newVal = !local.darkMode;
    const updated = { ...local, darkMode: newVal };
    setLocal(updated);
    writeSettingsCache(updated, window.__AGENCY_ID__);
    toggleTheme();
  };

  return (
    <div className="max-w-4xl mx-auto pb-20">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('settings')}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {lang === 'ar' ? 'إدارة إعدادات الشركة والتطبيق' : "Gérez les paramètres de votre entreprise et de l'application"}
        </p>
      </div>

      <div className="space-y-5">
        {/* Company Info */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
          <h2 className="font-bold text-lg mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
            <span>🏢</span>
            <span>{lang === 'ar' ? 'معلومات الشركة' : 'Informations de l\'entreprise'}</span>
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {lang === 'ar' 
              ? 'هذه المعلومات تظهر تلقائياً في العقود والفواتير'
              : 'Ces informations apparaissent automatiquement dans les contrats et factures'}
          </p>
          
          {/* Logo */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              🖼️ {lang === 'ar' ? 'الشعار (Logo)' : 'Logo de l\'entreprise'}
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              📐 {lang === 'ar'
                ? 'الحجم المنصوح: 300×300 بكسل (مربع) · PNG بخلفية شفافة'
                : 'Taille recommandée : 300×300 px (carré) · PNG fond transparent'}
            </p>
            <div className="flex items-center gap-4 flex-wrap">
              {local.logo && (
                <img 
                  src={local.logo} 
                  alt="Logo" 
                  className="h-16 w-16 object-contain border border-gray-200 dark:border-gray-600 rounded-lg p-1 bg-white" 
                />
              )}
              <label className="cursor-pointer bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 px-4 py-2 rounded-lg flex items-center gap-2 border border-blue-200 dark:border-blue-800">
                <Upload className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {local.logo 
                    ? (lang === 'ar' ? 'تغيير' : 'Changer')
                    : (lang === 'ar' ? 'إضافة الشعار' : 'Ajouter logo')}
                </span>
                <input type="hidden" name="dummy-logo" />
                <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" />
              </label>
              {local.logo && (
                <button 
                  onClick={() => setLocal({ ...local, logo: '' })}
                  className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 px-2 py-1 rounded text-sm font-medium border border-transparent"
                >
                  🗑️ {lang === 'ar' ? 'حذف' : 'Supprimer'}
                </button>
              )}
            </div>
          </div>
          
          {/* Company Stamp & Signature (managed by Super Admin only) */}
          <div className="mb-5 p-4 bg-purple-50 dark:bg-purple-900/10 border-2 border-purple-200 dark:border-purple-800/50 rounded-xl">
            <label className="block text-sm font-bold text-purple-900 dark:text-purple-300 mb-2 flex items-center gap-2">
              <span>✍️</span>
              <span>{lang === 'ar' ? 'ختم وتوقيع الشركة' : 'Cachet et Signature de l\'entreprise'}</span>
              {!isSA && (
                <span className="text-[10px] font-semibold text-purple-500 bg-purple-100 dark:bg-purple-900/30 px-2 py-0.5 rounded-full">
                  🔒 {lang === 'ar' ? 'المدير العام فقط' : 'Super Admin'}
                </span>
              )}
            </label>
            <p className="text-xs text-purple-700 dark:text-purple-400 mb-3">
              {lang === 'ar'
                ? 'صورة الختم والتوقيع تظهران تلقائياً في الفواتير الجديدة عند التفعيل'
                : 'Le cachet et la signature apparaissent automatiquement sur les nouvelles factures lorsque l\'option est activée'}
            </p>

            {/* Auto-insert toggle (Super Admin only) */}
            <div className="flex items-center justify-between gap-3 mb-4 p-3 bg-white dark:bg-gray-800/40 border border-purple-200 dark:border-purple-800/50 rounded-lg">
              <div>
                <div className="text-sm font-bold text-purple-900 dark:text-purple-300">
                  {lang === 'ar' ? 'إدراج تلقائي في الفواتير' : 'Insertion automatique sur les factures'}
                </div>
                <div className="text-[11px] text-purple-600 dark:text-purple-400">
                  {lang === 'ar' ? 'الفواتير القديمة لا تتغير' : 'Les factures existantes ne changent pas'}
                </div>
              </div>
              <button
                type="button"
                disabled={!isSA}
                onClick={toggleAutoStamp}
                className={`relative w-12 h-7 rounded-full transition-colors ${(local.autoStampSignature === true || local.autoStampSignature === 'true') ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'} ${!isSA ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                title={!isSA ? (lang === 'ar' ? 'المدير العام فقط' : 'Super Admin uniquement') : ''}
              >
                <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${(local.autoStampSignature === true || local.autoStampSignature === 'true') ? 'translate-x-5' : ''}`} />
              </button>
            </div>

            {/* Two image slots: Cachet + Signature */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { field: 'companyStamp',     onUp: uploadStamp,     labelFr: 'Cachet',    labelAr: 'الختم' },
                { field: 'companySignature', onUp: uploadSignature, labelFr: 'Signature', labelAr: 'التوقيع' },
              ].map(({ field, onUp, labelFr, labelAr }) => (
                <div key={field} className="p-3 bg-white dark:bg-gray-800/40 border border-purple-200 dark:border-purple-800/50 rounded-lg">
                  <div className="text-xs font-bold text-purple-800 dark:text-purple-300 mb-2">{lang === 'ar' ? labelAr : labelFr}</div>
                  <div className="flex items-center gap-3 flex-wrap">
                    {local[field]
                      ? <img src={local[field]} alt={labelFr} className="h-20 max-w-[160px] object-contain border-2 border-purple-300 dark:border-purple-700 rounded-lg p-2 bg-white shadow-sm" />
                      : <div className="h-20 w-[120px] flex items-center justify-center text-[11px] italic text-gray-400 border-2 border-dashed border-gray-300 rounded-lg">{lang === 'ar' ? 'فارغ' : 'Vide'}</div>}
                    {isSA && (
                      <div className="flex flex-col gap-2">
                        <label className="cursor-pointer bg-purple-500 hover:bg-purple-600 text-white px-3 py-1.5 rounded-lg flex items-center gap-1.5 font-bold shadow-sm text-xs">
                          <span>📷</span>
                          <span>{local[field] ? (lang === 'ar' ? 'تغيير' : 'Changer') : (lang === 'ar' ? 'إضافة' : 'Ajouter')}</span>
                          <input type="file" accept="image/*" onChange={onUp} className="hidden" />
                        </label>
                        {local[field] && (
                          <button
                            onClick={() => setLocal({ ...local, [field]: '' })}
                            className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 dark:border-red-800"
                          >
                            🗑️ {lang === 'ar' ? 'حذف' : 'Supprimer'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded text-xs text-blue-800 dark:text-blue-300">
              💡 {lang === 'ar'
                ? 'نصيحة: PNG بخلفية شفافة · الحجم المنصوح 400×200 بكسل · الحد الأقصى 2MB.'
                : 'Conseil : PNG fond transparent · taille recommandée 400×200 px · max 2MB.'}
            </div>
          </div>
          
          {/* Company Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label={'🏢 ' + (lang === 'ar' ? 'اسم الشركة' : 'Nom de l\'entreprise')}
              value={local.companyName}
              onChange={v => setLocal({ ...local, companyName: v })}
              placeholder="Kamel Auto Services"
            />
            {/* Gérant (owner) — used on the NARSA declaration "Je soussigné" block */}
            <Field
              label={'👤 ' + (lang === 'ar' ? 'الاسم العائلي للمسير' : 'Nom du gérant')}
              value={local.ownerLastName || ''}
              onChange={v => setLocal({ ...local, ownerLastName: v })}
              placeholder="HOUIDDA"
            />
            <Field
              label={'👤 ' + (lang === 'ar' ? 'الاسم الشخصي للمسير' : 'Prénom du gérant')}
              value={local.ownerFirstName || ''}
              onChange={v => setLocal({ ...local, ownerFirstName: v })}
              placeholder="Mohammed"
            />
            <Field 
              label={'📞 ' + (lang === 'ar' ? 'الهاتف' : 'Téléphone(s)')}
              value={local.companyPhone} 
              onChange={v => setLocal({ ...local, companyPhone: v })}
              placeholder="06.61.76.77.27 / 06.66.13.64.11"
            />
            <div className="md:col-span-2">
              <Field 
                label={'📍 ' + (lang === 'ar' ? 'العنوان' : 'Adresse')}
                value={local.companyAddress} 
                onChange={v => setLocal({ ...local, companyAddress: v })}
                placeholder="Av Dar Essalam Hay Etaaoune N°33 - Laayoune"
              />
            </div>
            <Field 
              label={'🏙️ ' + (lang === 'ar' ? 'المدينة' : 'Ville')}
              value={local.companyCity} 
              onChange={v => setLocal({ ...local, companyCity: v })}
              placeholder="Laayoune"
            />
            <Field 
              label={'📧 ' + (lang === 'ar' ? 'البريد الإلكتروني' : 'Email')}
              value={local.companyEmail} 
              onChange={v => setLocal({ ...local, companyEmail: v })}
              placeholder="contact@autoservices.ma"
            />
            <Field 
              label="🆔 ICE" 
              value={local.companyICE} 
              onChange={v => setLocal({ ...local, companyICE: v })}
              placeholder="000000000000000"
            />
            <Field 
              label={'📋 RC (' + (lang === 'ar' ? 'السجل التجاري' : 'Registre Commerce') + ')'}
              value={local.companyRC} 
              onChange={v => setLocal({ ...local, companyRC: v })}
              placeholder="12345"
            />
            <Field 
              label={'💼 IF (' + (lang === 'ar' ? 'الهوية الضريبية' : 'Identifiant Fiscal') + ')'}
              value={local.companyIF} 
              onChange={v => setLocal({ ...local, companyIF: v })}
              placeholder="12345678"
            />
          </div>
          
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              💰 {t('currency')}
            </label>
            <select 
              value={local.currency || 'MAD'} 
              onChange={e => setLocal({ ...local, currency: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="MAD">MAD - Dirham marocain</option>
              <option value="EUR">EUR - Euro</option>
              <option value="USD">USD - Dollar</option>
            </select>
          </div>
        </div>

        {/* Contract clauses (VERSO) — per-agency */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
          <ContractClausesSettings local={local} setLocal={setLocal} isAr={lang === 'ar'} />
        </div>

        {/* Contraventions NARSA */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
          <h2 className="font-bold text-lg mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
            <AlertCircle className="w-5 h-5 text-orange-600" />
            <span>{lang === 'ar' ? '⚠️ مخالفات NARSA' : '⚠️ Contraventions NARSA'}</span>
          </h2>
          <label className="flex items-center justify-between cursor-pointer p-3 bg-gray-50 dark:bg-gray-900/30 rounded-lg border border-gray-200 dark:border-gray-700">
            <div>
              <div className="font-semibold text-gray-900 dark:text-white text-sm">
                {lang === 'ar' ? '🌐 تفعيل زر NARSA' : '🌐 Activer bouton NARSA'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {lang === 'ar'
                  ? 'يظهر زر في صفحة المخالفات يفتح موقع NARSA مباشرة'
                  : 'Affiche un bouton dans Contraventions qui ouvre tasrih-saeq.narsa.gov.ma'}
              </div>
            </div>
            <input 
              type="checkbox"
              checked={!!local.narsaAutoCheck}
              onChange={e => setLocal({ ...local, narsaAutoCheck: e.target.checked })}
              className="w-5 h-5 accent-orange-600 cursor-pointer"
            />
          </label>
        </div>

        {/* Maintenance Config */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
          <h2 className="font-bold text-lg mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
            <span>🔧</span>
            <span>{lang === 'ar' ? 'إعدادات الصيانة والتنبيهات' : 'Paramètres de Maintenance & Alertes'}</span>
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {lang === 'ar' 
              ? 'تخصيص نسب التنبيه والمسافات الافتراضية لكل نوع من الصيانة.'
              : 'Personnalisez les seuils d\'alerte et les intervalles par défaut pour chaque type d\'entretien.'}
          </p>

          {/* Thresholds */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase dark:text-gray-400 mb-1">
                {lang === 'ar' ? 'حد التنبيه الأولي (%)' : 'Avertissement (%)'}
              </label>
              <input 
                type="number"
                min={50}
                max={100}
                value={local.maintWarningThreshold ?? 80}
                onChange={e => setLocal({ ...local, maintWarningThreshold: parseInt(e.target.value) || 80 })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-bold text-center"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase dark:text-gray-400 mb-1">
                {lang === 'ar' ? 'الحد الحرج (%)' : 'Alerte Critique (%)'}
              </label>
              <input 
                type="number"
                min={50}
                max={100}
                value={local.maintCriticalThreshold ?? 90}
                onChange={e => setLocal({ ...local, maintCriticalThreshold: parseInt(e.target.value) || 90 })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-bold text-center"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 uppercase dark:text-gray-400 mb-1">
                {lang === 'ar' ? 'حد فوات الأوان (%)' : 'Alerte Dépassé (%)'}
              </label>
              <input 
                type="number"
                min={100}
                max={150}
                value={local.maintOverdueThreshold ?? 100}
                onChange={e => setLocal({ ...local, maintOverdueThreshold: parseInt(e.target.value) || 100 })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-bold text-center"
              />
            </div>
          </div>

          {/* Default Intervals Customizer */}
          <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
            <h3 className="font-bold text-sm text-gray-800 dark:text-gray-200 mb-3">
              {lang === 'ar' ? 'المسافات الافتراضية للخدمات (كيلومتر)' : 'Intervalles par défaut (km)'}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {[
                { key: 'Vidange', fr: 'Vidange', ar: 'تغيير الزيت' },
                { key: 'Filtre à huile', fr: 'Filtre à huile', ar: 'فلتر الزيت' },
                { key: 'Filtre à air', fr: 'Filtre à air', ar: 'فلتر الهواء' },
                { key: 'Filtre à carburant', fr: 'Filtre carburant', ar: 'فلتر الوقود' },
                { key: 'Courroie de distribution', fr: 'Courroie distribution', ar: 'حزام التوقيت' },
                { key: 'Pneus', fr: 'Pneus', ar: 'الإطارات' },
                { key: 'Freins', fr: 'Freins', ar: 'الفرامل' },
                { key: 'Bougies', fr: 'Bougies', ar: 'الشمعات' },
                { key: 'Batterie', fr: 'Batterie', ar: 'البطارية' },
                { key: 'Climatisation', fr: 'Climatisation', ar: 'التكييف' },
                { key: 'Autre', fr: 'Autre', ar: 'أخرى' },
              ].map(item => {
                const intervals = local.maintIntervals || {};
                const val = intervals[item.key] ?? (
                  item.key === 'Vidange' || item.key === 'Filtre à huile' ? 10000 :
                  item.key === 'Filtre à air' || item.key === 'Climatisation' ? 20000 :
                  item.key === 'Filtre à carburant' || item.key === 'Freins' || item.key === 'Bougies' ? 30000 :
                  item.key === 'Pneus' ? 40000 :
                  item.key === 'Batterie' ? 60000 :
                  item.key === 'Courroie de distribution' ? 80000 : 10000
                );
                return (
                  <div key={item.key}>
                    <label className="block text-[10px] font-bold text-gray-500 dark:text-gray-400 mb-0.5 truncate" title={lang === 'ar' ? item.ar : item.fr}>
                      {lang === 'ar' ? item.ar : item.fr}
                    </label>
                    <input 
                      type="number"
                      value={val}
                      onChange={e => {
                        const updated = { ...local.maintIntervals, [item.key]: parseInt(e.target.value) || 0 };
                        setLocal({ ...local, maintIntervals: updated });
                      }}
                      className="w-full px-3 py-1.5 border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-xs outline-none font-semibold text-center"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ════════ TELEGRAM NOTIFICATIONS — Centralized Control Center ════════ */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">

          {/* Header + Master switch */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-700 dark:to-indigo-800 p-5 sm:p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-11 w-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0">
                  <Send className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <h2 className="font-bold text-lg sm:text-xl text-white leading-tight">
                    {lang === 'ar' ? 'إشعارات Telegram' : 'Notifications Telegram'}
                  </h2>
                  <p className="text-blue-100 text-xs mt-0.5">
                    {lang === 'ar'
                      ? 'مركز التحكم الموحّد لجميع إشعارات Telegram'
                      : 'Centre de contrôle unifié de toutes les notifications'}
                  </p>
                </div>
              </div>
              {/* Master toggle */}
              <button
                type="button"
                onClick={() => setLocal({ ...local, telegramEnabled: !telegramEnabled })}
                title={lang === 'ar' ? 'تفعيل / تعطيل كل الإشعارات' : 'Activer / désactiver toutes les notifications'}
                className={`w-14 h-7 rounded-full p-1 flex-shrink-0 transition-colors duration-200 ${
                  telegramEnabled ? 'bg-emerald-400' : 'bg-white/25'
                }`}
              >
                <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${
                  telegramEnabled
                    ? (lang === 'ar' ? '-translate-x-7' : 'translate-x-7')
                    : 'translate-x-0'
                }`} />
              </button>
            </div>
            <div className={`mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
              telegramEnabled ? 'bg-emerald-400/20 text-white' : 'bg-white/10 text-blue-100'
            }`}>
              <Power className="w-3.5 h-3.5" />
              {telegramEnabled
                ? (lang === 'ar' ? 'الإشعارات مُفعّلة' : 'Notifications activées')
                : (lang === 'ar' ? 'الإشعارات مُعطّلة' : 'Notifications désactivées')}
            </div>
          </div>

          <div className="p-4 sm:p-6 space-y-5">

            {/* ── Notification method (Telegram / Push / Both) ── */}
            <div className="rounded-xl border border-gray-100 dark:border-gray-700 p-4 sm:p-5">
              <div className="flex items-center gap-2 text-gray-900 dark:text-white font-bold text-sm mb-1">
                <span className="text-base">📢</span>
                <span>{lang === 'ar' ? 'طريقة الإشعار' : 'Méthode de notification'}</span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                {lang === 'ar'
                  ? 'اختر كيفية استقبال كل الإشعارات: عبر Telegram، أو إشعار فوري داخل التطبيق (Push)، أو الاثنين معاً.'
                  : 'Choisissez comment recevoir toutes les notifications : via Telegram, en push (application), ou les deux.'}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: 'telegram', icon: '✈️', fr: 'Telegram', ar: 'Telegram' },
                  { key: 'push', icon: '🔔', fr: 'Push', ar: 'إشعار فوري' },
                  { key: 'both', icon: '✅', fr: 'Les deux', ar: 'الاثنان' },
                ].map(opt => {
                  const active = (local.notificationChannel || 'telegram') === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setLocal({ ...local, notificationChannel: opt.key })}
                      className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-3 text-xs font-bold transition-colors ${
                        active
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-500'
                          : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-indigo-300'
                      }`}
                    >
                      <span className="text-lg">{opt.icon}</span>
                      <span>{lang === 'ar' ? opt.ar : opt.fr}</span>
                    </button>
                  );
                })}
              </div>
              {(local.notificationChannel === 'push' || local.notificationChannel === 'both') && (
                <div className="mt-3 rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-900/20 p-3">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-xs font-bold text-indigo-800 dark:text-indigo-300">
                      🔔 {lang === 'ar' ? 'الإشعارات الفورية على هذا الجهاز' : 'Push sur cet appareil'}
                    </span>
                    {(() => {
                      const map = {
                        granted: ['bg-green-100 text-green-700', lang === 'ar' ? 'مُفعّل' : 'Activé'],
                        denied: ['bg-red-100 text-red-700', lang === 'ar' ? 'مرفوض' : 'Refusé'],
                        default: ['bg-amber-100 text-amber-700', lang === 'ar' ? 'غير مُفعّل' : 'Inactif'],
                        unsupported: ['bg-gray-100 text-gray-600', lang === 'ar' ? 'غير مدعوم' : 'Non supporté'],
                      };
                      const [cls, label] = map[pushPerm] || map.default;
                      return <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${cls}`}>{label}</span>;
                    })()}
                  </div>
                  {pushSupp === false ? (
                    <p className="text-[11px] text-amber-600 dark:text-amber-400">
                      ⚠️ {lang === 'ar'
                        ? 'المتصفح غير متوافق أو إعداد Firebase ناقص (VITE_FIREBASE_*).'
                        : 'Navigateur non compatible ou configuration Firebase manquante (VITE_FIREBASE_*).'}
                    </p>
                  ) : (
                    <>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-2">
                        {lang === 'ar'
                          ? 'فعّل الإشعارات على كل جهاز تريد استقبالها عليه.'
                          : 'Activez sur chaque appareil où vous voulez recevoir les notifications.'}
                      </p>
                      {pushPerm !== 'granted' ? (
                        <button type="button" onClick={enablePush} disabled={pushBusy || pushSupp === false}
                          className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold disabled:opacity-60">
                          {pushBusy ? '⏳' : (lang === 'ar' ? 'تفعيل على هذا الجهاز' : 'Activer sur cet appareil')}
                        </button>
                      ) : (
                        <button type="button" onClick={disablePush} disabled={pushBusy}
                          className="px-3 py-1.5 bg-red-50 border border-red-200 text-red-700 rounded-lg text-xs font-bold disabled:opacity-60">
                          {lang === 'ar' ? 'تعطيل على هذا الجهاز' : 'Désactiver sur cet appareil'}
                        </button>
                      )}
                      {pushMsg && <div className="mt-2 text-[11px] font-bold text-indigo-700 dark:text-indigo-300">{pushMsg}</div>}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* ── Connexion (setup + Chat ID + test) ── */}
            <div className="rounded-xl border border-gray-100 dark:border-gray-700 p-4 sm:p-5 space-y-4">
              <div className="flex items-center gap-2 text-gray-900 dark:text-white font-bold text-sm">
                <span className="text-base">🔗</span>
                <span>{lang === 'ar' ? 'الربط والإعداد' : 'Connexion & configuration'}</span>
              </div>

              <details className="group">
                <summary className="cursor-pointer list-none flex items-center gap-2 text-xs font-bold text-blue-700 dark:text-blue-400 select-none">
                  <span>📲</span>
                  <span>{lang === 'ar' ? 'كيفية تفعيل الإشعارات (3 خطوات)' : 'Comment activer (3 étapes)'}</span>
                  <span className="ms-auto text-[10px] opacity-70 group-open:hidden">{lang === 'ar' ? 'إظهار' : 'Afficher'}</span>
                  <span className="ms-auto text-[10px] opacity-70 hidden group-open:inline">{lang === 'ar' ? 'إخفاء' : 'Masquer'}</span>
                </summary>
                <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-xl text-sm text-blue-900 dark:text-blue-300">
                  <ol className="list-decimal ms-5 space-y-2">
                    <li>
                      {lang === 'ar' ? 'افتح البوت الخاص بنا واضغط على ' : 'Ouvrez notre bot et appuyez sur '}
                      <b>START</b> :{' '}
                      <a href="https://t.me/track_carbot" target="_blank" rel="noopener noreferrer" className="font-bold text-blue-700 dark:text-blue-400 underline">
                        @track_carbot
                      </a>
                      {' — '}
                      <b className="text-red-600 dark:text-red-400">
                        {lang === 'ar' ? 'إجباري وإلا لن تعمل الإشعارات' : 'obligatoire, sinon les notifications ne marcheront pas'}
                      </b>
                    </li>
                    <li>
                      {lang === 'ar' ? 'للحصول على الـ Chat ID الخاص بك، افتح ' : 'Pour obtenir votre Chat ID, ouvrez '}
                      <a href="https://t.me/my_id_bot" target="_blank" rel="noopener noreferrer" className="font-bold text-blue-700 dark:text-blue-400 underline">
                        @my_id_bot
                      </a>
                      {lang === 'ar' ? ' وأرسل له أي رسالة — سيعطيك رقم Chat ID.' : ' et envoyez-lui un message — il vous donnera votre Chat ID.'}
                    </li>
                    <li>
                      {lang === 'ar' ? 'الصق الـ Chat ID في الحقل أدناه، ثم اضغط على ' : 'Collez le Chat ID dans le champ ci-dessous, puis cliquez sur '}
                      <b>{lang === 'ar' ? '«اختبار الإشعار».' : '« Tester notification ».'}</b>
                    </li>
                  </ol>
                  <div className="mt-3 text-xs text-blue-700 dark:text-blue-400">
                    🔒 {lang === 'ar'
                      ? 'البوت مُدار بأمان من طرف الخادم — لا تحتاج أي Token. فقط أدخل الـ Chat ID الخاص بك.'
                      : 'Le bot est géré en toute sécurité côté serveur — aucun token requis. Saisissez simplement votre Chat ID.'}
                  </div>
                </div>
              </details>

              <div>
                <Field
                  label={lang === 'ar' ? 'Chat ID (ممكن أكثر من واحد)' : 'Chat ID (plusieurs possibles)'}
                  value={local.telegramChatId}
                  onChange={v => setLocal({ ...local, telegramChatId: v })}
                  placeholder="9879877, 897877987, 7658766"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  👥 {lang === 'ar'
                    ? 'لإرسال الإشعار لأكثر من شخص، افصل بين الـ IDs بفاصلة: 9879877, 897877987'
                    : 'Pour notifier plusieurs personnes, séparez les IDs par une virgule : 9879877, 897877987'}
                </p>
              </div>

              <button
                onClick={handleTestTelegram}
                disabled={testingTelegram}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
              >
                <Send className="w-4 h-4" />
                {testingTelegram ? (lang === 'ar' ? 'جاري الإرسال...' : 'Envoi...') : (lang === 'ar' ? 'اختبار الإشعار' : 'Tester notification')}
              </button>

              {/* Notification language selector */}
              <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1.5">
                  <span>🌐</span>
                  {lang === 'ar' ? 'لغة الإشعارات' : 'Langue des notifications'}
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  {lang === 'ar'
                    ? 'اللغة المستعملة في جميع رسائل Telegram المرسلة (مستقلة عن لغة التطبيق).'
                    : 'Langue utilisée dans tous les messages Telegram envoyés (indépendante de la langue de l\'application).'}
                </p>
                <div className="grid grid-cols-3 gap-2 max-w-md">
                  {[
                    { val: 'ar', flag: '🇲🇦', fr: 'Arabe', en: 'Arabic', ar: 'العربية' },
                    { val: 'fr', flag: '🇫🇷', fr: 'Français', en: 'French', ar: 'الفرنسية' },
                    { val: 'en', flag: '🇬🇧', fr: 'Anglais', en: 'English', ar: 'الإنجليزية' },
                  ].map(opt => {
                    const active = (local.telegramLang || 'fr') === opt.val;
                    return (
                      <button
                        key={opt.val}
                        type="button"
                        onClick={() => setLocal({ ...local, telegramLang: opt.val })}
                        className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                          active
                            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-900 dark:text-blue-200'
                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-blue-300'
                        }`}
                      >
                        <span className="text-2xl leading-none">{opt.flag}</span>
                        <span className="text-xs font-black">
                          {lang === 'ar' ? opt.ar : (lang === 'en' ? opt.en : opt.fr)}
                        </span>
                        {active && <span className="text-[10px] text-blue-600 dark:text-blue-400 font-bold">✓</span>}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1.5">
                  {lang === 'ar'
                    ? 'الافتراضي: الفرنسية. تُحفظ مع الإعدادات وتُطبّق على مستوى الوكالة.'
                    : 'Par défaut : Français. Enregistrée avec les paramètres et appliquée à toute l\'agence.'}
                </p>
              </div>
            </div>

            {/* ── Gated body: everything below is governed by the master switch ── */}
            <div className={telegramEnabled ? 'space-y-5' : 'space-y-5 opacity-50 pointer-events-none select-none'}>

              {/* Toolbar: active count + bulk actions */}
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg flex items-center gap-2 text-xs">
                  <span className="text-blue-800 dark:text-blue-300 font-medium">
                    {lang === 'ar' ? 'المفعلة:' : 'Actives :'}
                  </span>
                  <span className="font-black text-blue-900 dark:text-blue-200">
                    {NOTIFICATION_KEYS.filter(k => getNotifEnabled(k)).length} / {NOTIFICATION_KEYS.length}
                  </span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={enableAllNotifications}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 hover:bg-green-200 dark:bg-green-900/30 dark:hover:bg-green-900/50 text-green-800 dark:text-green-300 rounded-lg text-xs font-bold transition"
                  >
                    ✅ {lang === 'ar' ? 'تفعيل الكل' : 'Activer tout'}
                  </button>
                  <button
                    onClick={disableAllNotifications}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 rounded-lg text-xs font-bold transition"
                  >
                    ❌ {lang === 'ar' ? 'تعطيل الكل' : 'Désactiver tout'}
                  </button>
                </div>
              </div>

              {/* Notification categories */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {NOTIFICATION_CATEGORIES.map(cat => (
                  <div key={cat.label_fr} className="border border-gray-100 dark:border-gray-700 rounded-xl overflow-hidden">
                    <div className="px-3 py-2.5 bg-gray-50 dark:bg-gray-700/50 flex items-center justify-between">
                      <span className="text-xs font-black text-gray-700 dark:text-gray-300 uppercase tracking-wider flex items-center gap-1.5">
                        <span className="text-sm">{cat.emoji}</span>
                        {lang === 'ar' ? cat.label_ar : cat.label_fr}
                      </span>
                      <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-2 py-0.5 rounded-full">
                        {cat.types.filter(t => getNotifEnabled(t.key)).length}/{cat.types.length}
                      </span>
                    </div>
                    <div className="divide-y divide-gray-50 dark:divide-gray-700">
                      {cat.types.map(notif => {
                        const isEnabled = getNotifEnabled(notif.key);
                        return (
                          <div
                            key={notif.key}
                            onClick={() => setNotifEnabled(notif.key, !isEnabled)}
                            className={`px-4 py-3 flex items-center justify-between gap-3 cursor-pointer transition select-none ${
                              isEnabled
                                ? 'bg-white dark:bg-gray-800 hover:bg-blue-50/50 dark:hover:bg-blue-900/10'
                                : 'bg-gray-50/70 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm font-bold ${isEnabled ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}>
                                {lang === 'ar' ? notif.ar : notif.fr}
                              </div>
                              <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                                {lang === 'ar' ? notif.desc_ar : notif.desc_fr}
                              </div>
                            </div>
                            <div className={`w-10 h-[22px] rounded-full p-0.5 flex-shrink-0 transition-colors duration-200 ${
                              isEnabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                            }`}>
                              <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                                isEnabled
                                  ? (lang === 'ar' ? '-translate-x-4' : 'translate-x-[22px]')
                                  : 'translate-x-0'
                              }`} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Speed Alerts (sub-card) ── */}
              <div className="border-2 border-orange-200 dark:border-orange-800/60 rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-orange-50 dark:bg-orange-900/10 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Gauge className="w-5 h-5 text-orange-500 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-black text-sm text-orange-900 dark:text-orange-300">
                        {lang === 'ar' ? 'تنبيهات السرعة' : 'Alertes de vitesse'}
                      </div>
                      <div className="text-[11px] text-orange-700 dark:text-orange-400">
                        {lang === 'ar' ? 'تنبيه عند تجاوز السرعة المحددة' : 'Alerte au dépassement de la limite'}
                      </div>
                    </div>
                  </div>
                  <div
                    onClick={() => setLocal({ ...local, gpsSpeedAlertEnabled: !local.gpsSpeedAlertEnabled })}
                    className={`w-12 h-6 rounded-full p-0.5 flex-shrink-0 transition-colors duration-200 cursor-pointer ${
                      local.gpsSpeedAlertEnabled ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                      local.gpsSpeedAlertEnabled
                        ? (lang === 'ar' ? '-translate-x-6' : 'translate-x-6')
                        : 'translate-x-0'
                    }`} />
                  </div>
                </div>

                {local.gpsSpeedAlertEnabled && (
                  <div className="p-4 space-y-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                        🏎️ {lang === 'ar' ? 'الحد الأقصى للسرعة (kph)' : 'Limite de vitesse (kph)'}
                      </label>
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="relative flex-1 min-w-[140px] max-w-[180px]">
                          <input
                            type="number"
                            min={30}
                            max={300}
                            step={5}
                            value={local.gpsSpeedLimit ?? 120}
                            onChange={e => {
                              const val = parseInt(e.target.value);
                              if (!isNaN(val)) {
                                setLocal({ ...local, gpsSpeedLimit: Math.min(300, Math.max(30, val)) });
                              }
                            }}
                            onBlur={e => {
                              const val = parseInt(e.target.value);
                              if (isNaN(val) || val < 30) setLocal({ ...local, gpsSpeedLimit: 30 });
                              else if (val > 300) setLocal({ ...local, gpsSpeedLimit: 300 });
                            }}
                            className="w-full px-4 py-3 border-2 border-orange-300 dark:border-orange-700 rounded-xl text-xl font-black text-center text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/10 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-orange-500 pointer-events-none">
                            kph
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {lang === 'ar' ? '(30 - 300 kph)' : '(30 à 300 kph)'}
                        </div>
                      </div>

                      {(local.gpsSpeedLimit ?? 120) < 30 || (local.gpsSpeedLimit ?? 120) > 300 ? (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-semibold">
                          ⚠️ {lang === 'ar' ? 'يجب أن تكون السرعة بين 30 و300 kph' : 'La vitesse doit être entre 30 et 300 kph'}
                        </p>
                      ) : (
                        <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                          ✓ {lang === 'ar'
                            ? `سيتم إرسال تنبيه عند تجاوز ${local.gpsSpeedLimit ?? 120} kph`
                            : `Alerte déclenchée si vitesse > ${local.gpsSpeedLimit ?? 120} kph`}
                        </p>
                      )}

                      <div className="mt-3 flex flex-wrap gap-2 items-center">
                        <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          {lang === 'ar' ? '⚡ سريع:' : '⚡ Rapide :'}
                        </span>
                        {[80, 100, 120, 140, 160].map(preset => (
                          <button
                            key={preset}
                            type="button"
                            onClick={() => setLocal({ ...local, gpsSpeedLimit: preset })}
                            className={`px-3 py-1.5 rounded-lg text-xs font-black transition-all border-2 ${
                              (local.gpsSpeedLimit ?? 120) === preset
                                ? 'bg-orange-500 text-white border-orange-500 shadow-sm'
                                : 'bg-white dark:bg-gray-800 border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300 hover:border-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20'
                            }`}
                          >
                            {preset} <span className="font-normal opacity-75">kph</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div
                      onClick={() => setLocal({ ...local, gpsSpeedAlertTelegram: !local.gpsSpeedAlertTelegram })}
                      className="flex items-center justify-between gap-3 cursor-pointer p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-xl select-none"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">📲</span>
                        <div>
                          <div className="font-bold text-sm text-blue-900 dark:text-blue-300">
                            {lang === 'ar' ? 'إشعار Telegram' : 'Notification Telegram'}
                          </div>
                          <div className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">
                            {lang === 'ar'
                              ? 'إرسال رسالة Telegram عند تجاوز السرعة'
                              : 'Envoyer un message Telegram lors du dépassement'}
                          </div>
                        </div>
                      </div>
                      <div className={`w-10 h-[22px] rounded-full p-0.5 flex-shrink-0 transition-colors duration-200 ${
                        local.gpsSpeedAlertTelegram !== false ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}>
                        <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                          local.gpsSpeedAlertTelegram !== false
                            ? (lang === 'ar' ? '-translate-x-4' : 'translate-x-[22px]')
                            : 'translate-x-0'
                        }`} />
                      </div>
                    </div>

                    <div className="p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-800 dark:text-amber-300">
                      <div className="font-bold mb-1">
                        💡 {lang === 'ar' ? 'ملاحظات:' : 'À savoir :'}
                      </div>
                      <ul className="space-y-1 list-none">
                        <li>• {lang === 'ar'
                          ? 'التنبيه يُرسل مرة واحدة لكل حدث (لا تكرار حتى تعود السرعة لطبيعتها)'
                          : 'L\'alerte n\'est envoyée qu\'une fois par excès (pas de spam, réinitialisée quand la vitesse redescend)'}
                        </li>
                        <li>• {lang === 'ar'
                          ? 'يشترط تفعيل GPS Tracking وإضافة Chat ID'
                          : 'GPS Tracking et un Chat ID Telegram sont requis pour les notifications'}
                        </li>
                        <li>• {lang === 'ar'
                          ? 'السرعة تُقرأ مباشرة من مزود GPS بوحدة kph (لا تحويل مطلوب)'
                          : 'La vitesse est lue directement depuis le fournisseur GPS en kph (aucune conversion)'}
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Geofencing (sub-card) ── */}
              <div className="border-2 border-purple-200 dark:border-purple-800/60 rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-purple-50 dark:bg-purple-900/10 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Building2 className="w-5 h-5 text-purple-500 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-black text-sm text-purple-900 dark:text-purple-300">
                        {lang === 'ar' ? 'الحدود الجغرافية (المدن)' : 'Géorepérage (villes)'}
                      </div>
                      <div className="text-[11px] text-purple-700 dark:text-purple-400">
                        {lang === 'ar' ? 'إشعار عند دخول/مغادرة سيارة لمدينة' : 'Alerte à l\'entrée/sortie d\'une ville'}
                      </div>
                    </div>
                  </div>
                  <div
                    onClick={() => setLocal({ ...local, geoNotifEnabled: !local.geoNotifEnabled })}
                    className={`w-12 h-6 rounded-full p-0.5 flex-shrink-0 transition-colors duration-200 cursor-pointer ${
                      local.geoNotifEnabled ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                      local.geoNotifEnabled
                        ? (lang === 'ar' ? '-translate-x-6' : 'translate-x-6')
                        : 'translate-x-0'
                    }`} />
                  </div>
                </div>

                {local.geoNotifEnabled && (
                  <div className="p-4 space-y-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                        📣 {lang === 'ar' ? 'نوع الإشعار' : 'Type de notification'}
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {[
                          { val: 'enter', icon: '📥', fr: 'Entrée en ville', ar: 'دخول المدينة', desc_fr: 'Notifie quand le véhicule entre dans une ville', desc_ar: 'إشعار عند دخول المدينة' },
                          { val: 'leave', icon: '📤', fr: 'Sortie de ville', ar: 'مغادرة المدينة', desc_fr: 'Notifie quand le véhicule quitte une ville', desc_ar: 'إشعار عند مغادرة المدينة' },
                          { val: 'both',  icon: '🔄', fr: 'Entrée & Sortie', ar: 'الدخول والخروج', desc_fr: 'Notifie les deux événements', desc_ar: 'إشعار للحالتين معاً' },
                        ].map(opt => {
                          const isActive = (local.geoNotifMode || 'enter') === opt.val;
                          return (
                            <button
                              key={opt.val}
                              type="button"
                              onClick={() => setLocal({ ...local, geoNotifMode: opt.val })}
                              className={`text-left p-3 rounded-xl border-2 transition-all ${
                                isActive
                                  ? 'bg-purple-100 dark:bg-purple-900/30 border-purple-500 text-purple-900 dark:text-purple-200'
                                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-purple-300'
                              }`}
                            >
                              <div className="text-lg mb-1">{opt.icon}</div>
                              <div className="text-xs font-black">{lang === 'ar' ? opt.ar : opt.fr}</div>
                              <div className="text-[10px] mt-0.5 opacity-70">{lang === 'ar' ? opt.desc_ar : opt.desc_fr}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div
                      onClick={() => setLocal({ ...local, geoNotifTelegram: !local.geoNotifTelegram })}
                      className="flex items-center justify-between gap-3 cursor-pointer p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-xl select-none"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">📲</span>
                        <div>
                          <div className="font-bold text-sm text-blue-900 dark:text-blue-300">
                            {lang === 'ar' ? 'إشعار Telegram' : 'Notification Telegram'}
                          </div>
                          <div className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">
                            {lang === 'ar'
                              ? 'إرسال رسالة Telegram عند تغيير المدينة'
                              : 'Envoyer un message Telegram lors du changement de ville'}
                          </div>
                        </div>
                      </div>
                      <div className={`w-10 h-[22px] rounded-full p-0.5 flex-shrink-0 transition-colors duration-200 ${
                        local.geoNotifTelegram !== false ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}>
                        <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                          local.geoNotifTelegram !== false
                            ? (lang === 'ar' ? '-translate-x-4' : 'translate-x-[22px]')
                            : 'translate-x-0'
                        }`} />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
                        🗺️ {lang === 'ar' ? 'تصفية حسب المدن (اختياري)' : 'Filtre par villes (optionnel)'}
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        {lang === 'ar'
                          ? 'اترك فارغاً لمراقبة كل المدن، أو أدخل أسماء المدن مفصولة بفاصلة.'
                          : 'Laissez vide pour surveiller toutes les villes, ou entrez des noms de villes séparés par des virgules.'}
                      </p>
                      <input
                        type="text"
                        value={local.geoNotifCities || ''}
                        onChange={e => setLocal({ ...local, geoNotifCities: e.target.value })}
                        placeholder={lang === 'ar'
                          ? 'مثال: الدار البيضاء، الرباط، أكادير'
                          : 'Ex: Casablanca, Rabat, Agadir, Marrakech'}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                      />
                      {(local.geoNotifCities || '').trim() && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {(local.geoNotifCities || '').split(',').map(c => c.trim()).filter(Boolean).map(city => (
                            <span key={city} className="text-[10px] font-bold px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full">
                              🏙️ {city}
                            </span>
                          ))}
                        </div>
                      )}
                      {!(local.geoNotifCities || '').trim() && (
                        <p className="text-[10px] text-green-700 dark:text-green-400 mt-1 font-semibold">
                          ✓ {lang === 'ar' ? 'جميع المدن مراقبة' : 'Toutes les villes surveillées'}
                        </p>
                      )}
                    </div>

                    <div className="p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-800 dark:text-amber-300">
                      <div className="font-bold mb-1">💡 {lang === 'ar' ? 'كيف يعمل:' : 'Comment ça marche :'}</div>
                      <ul className="space-y-1">
                        <li>• {lang === 'ar'
                          ? 'يستخدم النظام خدمة OpenStreetMap (Nominatim) مجانية لتحديد المدينة'
                          : 'Utilise OpenStreetMap (Nominatim) gratuit pour identifier la ville'}
                        </li>
                        <li>• {lang === 'ar'
                          ? 'يُرسل إشعار واحد فقط عند الانتقال من مدينة لأخرى (لا تكرار)'
                          : 'Une seule notification lors du passage d\'une ville à l\'autre (pas de spam)'}
                        </li>
                        <li>• {lang === 'ar'
                          ? 'يتطلب تفعيل GPS Tracking وإضافة Chat ID'
                          : 'GPS Tracking et un Chat ID Telegram sont requis'}
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                💡 {lang === 'ar'
                  ? 'التغييرات تُطبق فور الحفظ، دون الحاجة لإعادة التشغيل'
                  : 'Les changements s\'appliquent dès la sauvegarde, sans redémarrage'}
              </p>
            </div>
          </div>
        </div>

        {/* GPS Tracking */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
          <h2 className="font-bold text-lg mb-2 flex items-center gap-2 text-gray-900 dark:text-white">
            <MapPin className="w-5 h-5 text-green-600" />
            <span>{lang === 'ar' ? '📡 تتبع GPS' : '📡 GPS Tracking'}</span>
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {lang === 'ar'
              ? 'اختر مزود GPS الخاص بك في المغرب لمزامنة مواقع السيارات تلقائياً.'
              : 'Sélectionnez votre fournisseur GPS au Maroc pour synchroniser automatiquement les positions.'}
          </p>

          {/* Provider platform selector (isolated; default = GPSWOX/Traccar) */}
          <div className="mb-4">
            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1">
              🛰️ {lang === 'ar' ? 'منصة المزود' : 'Plateforme du fournisseur'}
            </label>
            <select
              value={local.gpsProvider || 'traccar'}
              onChange={e => setLocal({ ...local, gpsProvider: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 font-medium"
            >
              <option value="traccar">GPSWOX / Traccar ({lang === 'ar' ? 'الحالي' : 'actuel'})</option>
              <option value="afoulki">Afoulki GPS</option>
              <option value="iopgps">IOPGPS (iopgps.com)</option>
            </select>
            <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
              {lang === 'ar'
                ? 'كل مزود معزول تماماً — اختيار أي مزود لا يؤثر على إعدادات المزودين الآخرين.'
                : 'Chaque fournisseur est isolé — choisir un fournisseur n\'affecte pas vos autres configurations.'}
            </p>
          </div>

          {/* ── Afoulki GPS (isolated module) ── */}
          {(local.gpsProvider || 'traccar') === 'afoulki' && (
            <div className="mb-4 p-4 bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-200 dark:border-indigo-800 rounded-xl space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!local.afoulkiEnabled}
                  onChange={e => setLocal({ ...local, afoulkiEnabled: e.target.checked })}
                  className="w-5 h-5 cursor-pointer accent-indigo-600"
                />
                <div>
                  <div className="font-bold text-sm text-indigo-900 dark:text-indigo-300">
                    {lang === 'ar' ? '✓ تفعيل Afoulki GPS' : '✓ Activer Afoulki GPS'}
                  </div>
                  <div className="text-xs text-indigo-700 dark:text-indigo-400 mt-0.5">
                    {lang === 'ar' ? 'مزامنة مستقلة عبر منصة Afoulki' : 'Synchronisation indépendante via la plateforme Afoulki'}
                  </div>
                </div>
              </label>
              {local.afoulkiEnabled && (
                <div className="space-y-3">
                  <Field
                    label={lang === 'ar' ? 'رابط خادم Afoulki (URL)' : 'URL du serveur Afoulki'}
                    value={local.afoulkiUrl || 'https://afoulki.gps1.ma'}
                    onChange={v => setLocal({ ...local, afoulkiUrl: v })}
                    placeholder="https://afoulki.gps1.ma"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field
                      label={lang === 'ar' ? '👤 البريد/المستخدم' : '👤 Email / Utilisateur'}
                      value={local.afoulkiEmail || ''}
                      onChange={v => setLocal({ ...local, afoulkiEmail: v })}
                      placeholder={lang === 'ar' ? 'ljscar أو email@...' : 'ljscar ou email@...'}
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {lang === 'ar' ? '🔒 كلمة السر' : '🔒 Mot de passe'}
                      </label>
                      <input
                        type="password"
                        value={local.afoulkiPassword || ''}
                        onChange={e => setLocal({ ...local, afoulkiPassword: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-indigo-500"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={handleTestAfoulki}
                      disabled={testingAfoulki}
                      className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2"
                    >
                      <Wifi className="w-4 h-4" />
                      <span>{testingAfoulki ? (lang === 'ar' ? 'جاري الاختبار...' : 'Test...') : (lang === 'ar' ? 'اختبار وعدّ السيارات' : 'Tester / compter les véhicules')}</span>
                    </button>
                    {afoulkiTestMsg && (
                      <span className={`text-xs font-bold px-2 py-1 rounded-lg ${afoulkiTestMsg.ok ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
                        {afoulkiTestMsg.text}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-indigo-700/80 dark:text-indigo-400 leading-snug">
                    {lang === 'ar'
                      ? 'منصة Afoulki (gps1.ma) تعمل بنفس بروتوكول GPSWOX — أدخل البريد وكلمة السر فقط.'
                      : 'La plateforme Afoulki (gps1.ma) utilise le protocole GPSWOX — saisissez simplement email et mot de passe.'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* ── IOPGPS (isolated module) ── */}
          {(local.gpsProvider || 'traccar') === 'iopgps' && (
            <div className="mb-4 p-4 bg-teal-50 dark:bg-teal-900/10 border border-teal-200 dark:border-teal-800 rounded-xl space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!local.iopgpsEnabled}
                  onChange={e => setLocal({ ...local, iopgpsEnabled: e.target.checked })}
                  className="w-5 h-5 cursor-pointer accent-teal-600"
                />
                <div>
                  <div className="font-bold text-sm text-teal-900 dark:text-teal-300">
                    {lang === 'ar' ? '✓ تفعيل IOPGPS' : '✓ Activer IOPGPS'}
                  </div>
                  <div className="text-xs text-teal-700 dark:text-teal-400 mt-0.5">
                    {lang === 'ar' ? 'مزامنة مستقلة عبر منصة iopgps.com' : 'Synchronisation indépendante via la plateforme iopgps.com'}
                  </div>
                </div>
              </label>
              {local.iopgpsEnabled && (
                <div className="space-y-3">
                  <Field
                    label={lang === 'ar' ? 'رابط خادم IOPGPS (URL)' : 'URL du serveur IOPGPS'}
                    value={local.iopgpsUrl || 'https://www.iopgps.com'}
                    onChange={v => setLocal({ ...local, iopgpsUrl: v })}
                    placeholder="https://www.iopgps.com"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field
                      label={lang === 'ar' ? '👤 البريد / اسم الحساب' : '👤 Email / Nom de compte'}
                      value={local.iopgpsEmail || ''}
                      onChange={v => setLocal({ ...local, iopgpsEmail: v })}
                      placeholder={lang === 'ar' ? 'nom_compte أو email@...' : 'nom_compte ou email@...'}
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {lang === 'ar' ? '🔒 كلمة السر / Clé Secrète' : '🔒 Mot de passe / Clé Secrète'}
                      </label>
                      <input
                        type="password"
                        value={local.iopgpsPassword || ''}
                        onChange={e => setLocal({ ...local, iopgpsPassword: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-teal-500"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      onClick={handleTestIopgps}
                      disabled={testingIopgps}
                      className="bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2"
                    >
                      <Wifi className="w-4 h-4" />
                      <span>{testingIopgps ? (lang === 'ar' ? 'جاري الاختبار...' : 'Test...') : (lang === 'ar' ? 'اختبار وعدّ السيارات' : 'Tester / compter les véhicules')}</span>
                    </button>
                    {iopgpsTestMsg && (
                      <span className={`text-xs font-bold px-2 py-1 rounded-lg ${iopgpsTestMsg.ok ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
                        {iopgpsTestMsg.text}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-teal-700/80 dark:text-teal-400 leading-snug">
                    {lang === 'ar'
                      ? 'منصة IOPGPS (iopgps.com) تدعم Open API و GPSWOX تلقائياً — أدخل الحساب وكلمة السر.'
                      : 'La plateforme IOPGPS (iopgps.com) supporte l\'Open API et GPSWOX automatiquement — saisissez vos identifiants.'}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* GPSWOX / Traccar (existing — unchanged), hidden when Afoulki or IOPGPS is selected */}
          {(local.gpsProvider || 'traccar') !== 'afoulki' && (local.gpsProvider || 'traccar') !== 'iopgps' && (<>
          <label className="flex items-center gap-3 cursor-pointer p-3 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg mb-4">
            <input 
              type="checkbox"
              checked={!!local.traccarEnabled}
              onChange={e => setLocal({ ...local, traccarEnabled: e.target.checked })}
              className="w-5 h-5 cursor-pointer accent-green-600"
            />
            <div>
              <div className="font-bold text-sm text-green-900 dark:text-green-300">
                {lang === 'ar' ? '✓ تفعيل GPS Tracking' : '✓ Activer GPS Tracking'}
              </div>
              <div className="text-xs text-green-700 dark:text-green-400 mt-0.5">
                {lang === 'ar' 
                  ? 'مزامنة المواقع تلقائياً من الخادم'
                  : 'Synchronisation automatique des positions depuis le serveur'}
              </div>
            </div>
          </label>
          
          {local.traccarEnabled && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                  🇲🇦 {lang === 'ar' ? 'مزود GPS' : 'Fournisseur GPS'}
                </label>
                <select 
                  value={local.traccarProvider || 'custom'}
                  onChange={e => {
                    const provider = e.target.value;
                    const providers = {
                      athuraya: 'https://athuraya.ddns.net',
                      somayar: 'https://somafleet.somayar.ma',
                      stg: 'https://app.stgtelematics.com',
                      spirea: 'https://traceurgpsmaroc.com',
                      geomada: 'https://www.geomada.ma',
                      webtech: 'https://webtechgps.com',
                      geocartis: 'https://geocartis.ma',
                      cartrack: 'https://web.cartrack.ma',
                      dasauto: 'https://www.das-auto.ma',
                      tracky: 'https://web.tracky.ma',
                      iopgps: 'https://www.iopgps.com',
                      custom: ''
                    };
                    setLocal({
                      ...local,
                      traccarProvider: provider,
                      // Route Tracky / IOPGPS to their dedicated adapters; all GPSWOX presets
                      // keep the standard traccar adapter.
                      gpsProvider: provider === 'tracky' ? 'tracky' : (provider === 'iopgps' ? 'iopgps' : 'traccar'),
                      traccarUrl: providers[provider] !== undefined ? providers[provider] : local.traccarUrl
                    });
                  }}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 font-medium"
                >
                  <option value="athuraya">🛰️ Athuraya GPS (athuraya.ddns.net)</option>
                  <option value="somayar">🚗 SOMAYAR (somafleet.somayar.ma)</option>
                  <option value="stg">📡 STG Telematics (Maroc)</option>
                  <option value="spirea">🌍 Spirea GPS Maroc</option>
                  <option value="geomada">📍 Geomada (geomada.ma)</option>
                  <option value="webtech">💻 WebTech GPS</option>
                  <option value="geocartis">🗺️ Geocartis (geocartis.ma)</option>
                  <option value="cartrack">🚙 Cartrack Maroc</option>
                  <option value="dasauto">🚗 DAS AUTO (das-auto.ma)</option>
                  <option value="tracky">📍 Tracky (web.tracky.ma)</option>
                  <option value="iopgps">📍 IOPGPS (iopgps.com)</option>
                  <option value="custom">⚙️ {lang === 'ar' ? 'آخر / مخصص' : 'Autre / Personnalisé'}</option>
                </select>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                  {lang === 'ar' 
                    ? '💡 معظم مزودي GPS في المغرب يستخدمون نظام GPSWOX أو Traccar'
                    : '💡 La plupart des fournisseurs GPS au Maroc utilisent GPSWOX ou Traccar'}
                </p>
              </div>
              
              <Field 
                label={lang === 'ar' ? 'رابط الخادم (URL)' : 'URL du serveur'} 
                value={local.traccarUrl} 
                onChange={v => setLocal({ ...local, traccarUrl: v })} 
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field 
                  label={lang === 'ar' ? '👤 البريد أو اسم المستخدم' : '👤 Email ou Username'} 
                  value={local.traccarEmail} 
                  onChange={v => setLocal({ ...local, traccarEmail: v })}
                  placeholder="client+1074 ou email@..."
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {lang === 'ar' ? '🔑 كلمة السر' : '🔑 Mot de passe'}
                  </label>
                  <input 
                    type="password"
                    value={local.traccarPassword || ''}
                    onChange={e => setLocal({ ...local, traccarPassword: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {lang === 'ar' ? 'تحديث المواقع كل (ثانية)' : 'Fréquence de rafraîchissement (secondes)'}
                </label>
                <select 
                  value={local.traccarRefreshSec || 30} 
                  onChange={e => setLocal({ ...local, traccarRefreshSec: parseInt(e.target.value) })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500" 
                >
                  <option value={10}>10 secondes (rapide)</option>
                  <option value={30}>30 secondes (recommandé)</option>
                  <option value={60}>1 minute</option>
                  <option value={300}>5 minutes (économique)</option>
                </select>
              </div>

              {/* Stop / Stationary detection */}
              <div className="p-3 bg-indigo-50 dark:bg-indigo-900/10 border border-indigo-200 dark:border-indigo-800 rounded-lg">
                <div className="text-xs font-bold text-indigo-900 dark:text-indigo-300 mb-2 flex items-center gap-1.5">
                  <span>⏱️</span>
                  <span>{lang === 'ar' ? 'كشف توقّف السيارة' : 'Détection d\'arrêt du véhicule'}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-400 mb-1">
                      {lang === 'ar' ? 'مدة التوقف للتنبيه (دقائق)' : 'Seuil d\'arrêt (minutes)'}
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={local.gpsStopThresholdMin ?? 30}
                      onChange={e => setLocal({ ...local, gpsStopThresholdMin: Math.max(1, parseInt(e.target.value) || 0) })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="30"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold text-gray-600 dark:text-gray-400 mb-1">
                      {lang === 'ar' ? 'نصف قطر الدقة (متر)' : 'Rayon de précision (mètres)'}
                    </label>
                    <input
                      type="number"
                      min="10"
                      value={local.gpsStopRadiusM ?? 60}
                      onChange={e => setLocal({ ...local, gpsStopRadiusM: Math.max(10, parseInt(e.target.value) || 0) })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg text-sm font-semibold outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="60"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-indigo-700 dark:text-indigo-400 mt-2 leading-snug">
                  {lang === 'ar'
                    ? 'تُعتبر السيارة متوقفة إذا بقيت داخل نصف القطر المحدد. عند تجاوز مدة التوقف، تُميَّز على الخريطة.'
                    : 'Le véhicule est considéré arrêté tant qu\'il reste dans ce rayon. Au-delà du seuil, il est mis en évidence sur la carte.'}
                </p>
              </div>

              {/* Custom CORS Proxy */}
              <div className="p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg">
                <label className="block text-xs font-bold text-blue-900 dark:text-blue-300 mb-1 flex items-center gap-1">
                  <span>🔒</span>
                  <span>{lang === 'ar' ? 'CORS Proxy مخصص (متقدم)' : 'CORS Proxy personnalisé (avancé)'}</span>
                </label>
                <input 
                  type="text"
                  value={local.corsProxy || ''}
                  onChange={e => setLocal({ ...local, corsProxy: e.target.value })}
                  placeholder="https://votre-proxy.com/?url="
                  className="w-full px-3 py-1.5 border border-blue-300 dark:border-blue-700 rounded text-xs font-mono bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <p className="text-[10px] text-blue-700 dark:text-blue-400 mt-1.5 leading-snug">
                  {lang === 'ar' 
                    ? '⚠️ إذا كان الخادم يحجب CORS. اتركه فارغاً للاستخدام التلقائي.'
                    : '⚠️ Si le serveur GPS bloque CORS. Laisser vide pour utiliser les proxies publics gratuits.'}
                </p>
              </div>
              
              {/* Test Connection Button */}
              <button 
                onClick={handleTestTraccar}
                disabled={testingConnection}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
              >
                <Wifi className="w-4 h-4" />
                <span>{testingConnection ? (lang === 'ar' ? 'جاري الاتصال...' : 'Connexion...') : (lang === 'ar' ? 'اختبار الاتصال' : 'Tester la connexion')}</span>
              </button>
              
              {/* Warning Info */}
              <div className="p-3 bg-red-50 dark:bg-red-900/10 border-2 border-red-200 dark:border-red-800 rounded-lg text-xs text-red-900 dark:text-red-300">
                <div className="font-bold mb-2 flex items-center gap-1 text-sm">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <span>{lang === 'ar' ? '⚠️ تنبيه مهم - مشاكل محتملة:' : '⚠️ Avertissement - Problèmes possibles:'}</span>
                </div>
                <div className="space-y-2">
                  <div>
                    <strong>1. CORS: </strong>
                    {lang === 'ar' 
                      ? 'إذا رأيت "Load failed"، يحتاج المدير لإضافة web.origin'
                      : 'Si "Load failed", l\'admin doit ajouter web.origin'}
                  </div>
                  <div>
                    <strong>2. Allowlist: </strong>
                    {lang === 'ar' 
                      ? 'إذا رأيت "Host not in allowlist"، عنوان IP الخاص بك محظور'
                      : 'Si "Host not in allowlist", votre IP est bloquée par le serveur'}
                  </div>
                  <div className="mt-2 p-2 bg-white dark:bg-gray-900 border border-red-100 dark:border-red-800 rounded font-mono text-[10px] text-gray-900 dark:text-gray-300">
                    sudo nano /opt/traccar/conf/traccar.xml
                    <br />
                    &lt;entry key='web.origin'&gt;*&lt;/entry&gt;
                    <br />
                    &lt;entry key='filter.client.allowlist'&gt;&lt;/entry&gt;
                    <br />
                    sudo systemctl restart traccar
                  </div>
                </div>
              </div>
              
              <div className="p-3 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg text-xs text-blue-800 dark:text-blue-300">
                <div className="font-bold mb-1">
                  {lang === 'ar' ? '💡 كيفية الربط:' : '💡 Comment associer:'}
                </div>
                <div>
                  {lang === 'ar' 
                    ? 'في صفحة "السيارات"، أضف "معرف Traccar" (Unique ID) لكل سيارة لتزامن موقعها تلقائياً. عادة يكون رقم IMEI أو رقم الجهاز.'
                    : 'Dans la page "Voitures", ajoutez le "Traccar Unique ID" (généralement IMEI ou ID de l\'appareil) pour chaque voiture afin de synchroniser sa position automatiquement.'}
                </div>
              </div>
            </div>
          )}
          </>)}
        </div>

        {/* Profit/Loss calculation config */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 border-2 border-emerald-200 dark:border-emerald-800">
          <h2 className="font-bold text-lg mb-2 flex items-center gap-2 text-gray-900 dark:text-white">
            <TrendingUp className="w-5 h-5 text-emerald-600" />
            <span>{lang === 'ar' ? '📊 حساب الأرباح والخسائر' : '📊 Calcul des Bénéfices / Pertes'}</span>
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            {lang === 'ar'
              ? 'اختر المصادر التي تريد احتسابها في الربح الصافي. كل مصدر مفعّل سيتم خصمه أو إضافته تلقائياً.'
              : 'Cochez les sources à inclure dans le calcul du bénéfice net. Chaque source activée sera automatiquement comptabilisée.'}
          </p>
          <div className="p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-800 dark:text-amber-300 mb-4 flex items-start gap-2">
            <span className="text-lg">💡</span>
            <span>
              {lang === 'ar'
                ? 'نصيحة: فعّل جميع المصادر لحساب دقيق للأرباح الحقيقية. إذا أزلت التفعيل عن مصدر، لن يُحتسب في صفحة المالية.'
                : 'Conseil: Activez toutes les sources pour un calcul précis des bénéfices réels. Si vous désactivez une source, elle ne sera pas comptée dans la page Finances.'}
            </span>
          </div>
          
          <div className="space-y-1">
            <div className="text-xs font-bold text-red-600 dark:text-red-400 uppercase tracking-wider mb-2 mt-2 flex items-center gap-2">
              <span>📤</span>
              <span>{lang === 'ar' ? 'المصاريف (تُخصم من الأرباح)' : 'CHARGES (déduites des bénéfices)'}</span>
            </div>
            
            {/* Vidanges */}
            <label className="flex items-center gap-3 cursor-pointer p-2.5 hover:bg-gray-50 dark:hover:bg-gray-900/30 rounded-lg transition border border-transparent hover:border-gray-200 dark:hover:border-gray-700">
              <input 
                type="checkbox"
                checked={local.financeIncludeVidanges !== false}
                onChange={e => setLocal({ ...local, financeIncludeVidanges: e.target.checked })}
                className="w-5 h-5 cursor-pointer accent-emerald-600"
              />
              <div className="flex-1">
                <span className="block font-bold text-sm text-gray-800 dark:text-gray-200">
                  🔧 {lang === 'ar' ? 'الصيانة وتغيير الزيت' : 'Vidanges & Entretien'}
                </span>
                <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                  {lang === 'ar' ? 'تكاليف صيانة السيارات' : 'Coûts d\'entretien des véhicules'}
                </span>
              </div>
              <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 rounded-full font-bold">vidanges</span>
            </label>
            
            {/* Credit voitures */}
            <label className="flex items-center gap-3 cursor-pointer p-2.5 hover:bg-gray-50 dark:hover:bg-gray-900/30 rounded-lg transition border border-transparent hover:border-gray-200 dark:hover:border-gray-700">
              <input 
                type="checkbox"
                checked={local.financeIncludeCredits !== false}
                onChange={e => setLocal({ ...local, financeIncludeCredits: e.target.checked })}
                className="w-5 h-5 cursor-pointer accent-emerald-600"
              />
              <div className="flex-1">
                <span className="block font-bold text-sm text-gray-800 dark:text-gray-200">
                  💳 {lang === 'ar' ? 'أقساط السيارات' : 'Crédits voitures'}
                </span>
                <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                  {lang === 'ar' ? 'الدفعات المسددة للقروض' : 'Paiements des crédits auto'}
                </span>
              </div>
              <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300 rounded-full font-bold">creditPayments</span>
            </label>
            
            {/* General expenses */}
            <label className="flex items-center gap-3 cursor-pointer p-2.5 hover:bg-gray-50 dark:hover:bg-gray-900/30 rounded-lg transition border border-transparent hover:border-gray-200 dark:hover:border-gray-700">
              <input 
                type="checkbox"
                checked={local.financeIncludeExpenses !== false}
                onChange={e => setLocal({ ...local, financeIncludeExpenses: e.target.checked })}
                className="w-5 h-5 cursor-pointer accent-emerald-600"
              />
              <div className="flex-1">
                <span className="block font-bold text-sm text-gray-800 dark:text-gray-200">
                  📦 {lang === 'ar' ? 'المصاريف العامة' : 'Dépenses générales'}
                </span>
                <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                  {lang === 'ar' ? 'الكراء، التأمين، الغسيل، الوقود، إلخ' : 'Loyer, Assurance, Lavage, Carburant, etc.'}
                </span>
              </div>
              <span className="text-xs px-2 py-0.5 bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300 rounded-full font-bold">expenses</span>
            </label>
            
            {/* Staff salaries */}
            <label className="flex items-center gap-3 cursor-pointer p-2.5 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 rounded-lg transition border-2 border-dashed border-emerald-300 dark:border-emerald-800">
              <input 
                type="checkbox"
                checked={local.financeIncludeStaff !== false}
                onChange={e => setLocal({ ...local, financeIncludeStaff: e.target.checked })}
                className="w-5 h-5 cursor-pointer accent-emerald-600"
              />
              <div className="flex-1">
                <span className="block font-bold text-sm text-gray-800 dark:text-gray-200">
                  👥 {lang === 'ar' ? 'رواتب الموظفين' : 'Salaires du personnel'}
                </span>
                <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                  {lang === 'ar' ? 'جميع الرواتب المدفوعة (شهرية/يومية)' : 'Tous les salaires versés (mensuels/journaliers)'}
                </span>
              </div>
              <span className="text-xs px-2 py-0.5 bg-green-200 text-green-900 dark:bg-green-900/30 dark:text-green-300 rounded-full font-bold">⭐ NEW</span>
            </label>
            
            {/* Staff Advances */}
            <label className="flex items-center gap-3 cursor-pointer p-2.5 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 rounded-lg transition border-2 border-dashed border-emerald-300 dark:border-emerald-800">
              <input 
                type="checkbox"
                checked={local.financeIncludeStaffAdvances !== false}
                onChange={e => setLocal({ ...local, financeIncludeStaffAdvances: e.target.checked })}
                className="w-5 h-5 cursor-pointer accent-emerald-600"
              />
              <div className="flex-1">
                <span className="block font-bold text-sm text-gray-800 dark:text-gray-200">
                  ⏩ {lang === 'ar' ? 'سلفات الموظفين' : 'Avances du personnel'}
                </span>
                <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                  {lang === 'ar' ? 'السلفات والمكافآت المدفوعة' : 'Avances et primes versées aux employés'}
                </span>
              </div>
              <span className="text-xs px-2 py-0.5 bg-green-200 text-green-900 dark:bg-green-900/30 dark:text-green-300 rounded-full font-bold">⭐ NEW</span>
            </label>
            
            {/* Cheques Paid */}
            <label className="flex items-center gap-3 cursor-pointer p-2.5 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 rounded-lg transition border-2 border-dashed border-emerald-300 dark:border-emerald-800">
              <input 
                type="checkbox"
                checked={local.financeIncludeChequesPaid !== false}
                onChange={e => setLocal({ ...local, financeIncludeChequesPaid: e.target.checked })}
                className="w-5 h-5 cursor-pointer accent-emerald-600"
              />
              <div className="flex-1">
                <span className="block font-bold text-sm text-gray-800 dark:text-gray-200">
                  📤 {lang === 'ar' ? 'الشيكات الصادرة (المصروفة)' : 'Chèques émis (encaissés)'}
                </span>
                <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                  {lang === 'ar' ? 'الشيكات المدفوعة والمصروفة فقط' : 'Uniquement les chèques émis et encaissés'}
                </span>
              </div>
              <span className="text-xs px-2 py-0.5 bg-green-200 text-green-900 dark:bg-green-900/30 dark:text-green-300 rounded-full font-bold">⭐ NEW</span>
            </label>
            
            {/* Bank installments */}
            <label className="flex items-center gap-3 cursor-pointer p-2.5 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 rounded-lg transition border-2 border-dashed border-emerald-300 dark:border-emerald-800">
              <input 
                type="checkbox"
                checked={local.financeIncludeBankInstallments !== false}
                onChange={e => setLocal({ ...local, financeIncludeBankInstallments: e.target.checked })}
                className="w-5 h-5 cursor-pointer accent-emerald-600"
              />
              <div className="flex-1">
                <span className="block font-bold text-sm text-gray-800 dark:text-gray-200">
                  🏦 {lang === 'ar' ? 'الأقساط البنكية (الترايتات)' : 'Traites bancaires'}
                </span>
                <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                  {lang === 'ar' ? 'الأقساط البنكية المدفوعة' : 'Échéances bancaires payées'}
                </span>
              </div>
              <span className="text-xs px-2 py-0.5 bg-green-200 text-green-900 dark:bg-green-900/30 dark:text-green-300 rounded-full font-bold">⭐ NEW</span>
            </label>
            
            <div className="border-t border-gray-200 dark:border-gray-700 my-3" />
            
            <div className="text-xs font-bold text-green-600 dark:text-green-400 uppercase tracking-wider mb-2 flex items-center gap-2">
              <span>📥</span>
              <span>{lang === 'ar' ? 'المداخيل (تُضاف للأرباح)' : 'REVENUS (ajoutés aux bénéfices)'}</span>
            </div>
            
            {/* Cheques Received */}
            <label className="flex items-center gap-3 cursor-pointer p-2.5 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 rounded-lg transition border-2 border-dashed border-emerald-300 dark:border-emerald-800">
              <input 
                type="checkbox"
                checked={local.financeIncludeChequesReceived !== false}
                onChange={e => setLocal({ ...local, financeIncludeChequesReceived: e.target.checked })}
                className="w-5 h-5 cursor-pointer accent-emerald-600"
              />
              <div className="flex-1">
                <span className="block font-bold text-sm text-gray-800 dark:text-gray-200">
                  📥 {lang === 'ar' ? 'الشيكات المستلمة (المصروفة)' : 'Chèques reçus (encaissés)'}
                </span>
                <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                  {lang === 'ar' ? 'الشيكات المستلمة والمصروفة فقط' : 'Uniquement les chèques reçus et encaissés'}
                </span>
              </div>
              <span className="text-xs px-2 py-0.5 bg-green-200 text-green-900 dark:bg-green-900/30 dark:text-green-300 rounded-full font-bold">⭐ NEW</span>
            </label>
          </div>
        </div>


        {/* Dark Mode */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6">
          <h2 className="font-bold text-lg mb-4 flex items-center gap-2 text-gray-900 dark:text-white">
            <span>🌙</span>
            <span>{lang === 'ar' ? 'المظهر' : 'Apparence'}</span>
          </h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-800 dark:text-gray-200 text-sm">
                {lang === 'ar' ? 'الوضع الليلي (Dark Mode)' : 'Mode sombre (Dark Mode)'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {lang === 'ar' ? 'خلفية داكنة لراحة العينين' : 'Fond sombre pour réduire la fatigue visuelle'}
              </p>
            </div>
            <button 
              onClick={handleToggleTheme}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors border-none cursor-pointer ${
                local.darkMode ? 'bg-indigo-600' : 'bg-gray-300'
              }`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                local.darkMode ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>

        {/* Clear Data (danger zone) */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl border-2 border-red-100 dark:border-red-900/40 shadow-sm p-6">
          <h2 className="font-bold text-lg mb-2 flex items-center gap-2 text-red-700 dark:text-red-400">
            <Trash2 className="w-5 h-5" />
            <span>{lang === 'ar' ? 'مسح البيانات' : 'Effacer les données'}</span>
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {lang === 'ar'
              ? 'احذف بيانات وكالتك بشكل انتقائي (سيارات، عملاء، عقود…). الحذف نهائي.'
              : 'Supprimez sélectivement les données de votre agence (voitures, clients, contrats…). Action irréversible.'}
          </p>
          <button
            onClick={() => setShowClearData(true)}
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl font-semibold shadow-sm transition-colors border-none"
          >
            <Trash2 className="w-4 h-4" />
            {lang === 'ar' ? 'اختيار البيانات للحذف' : 'Choisir les données à effacer'}
          </button>
        </div>
      </div>

      {showClearData && <ClearDataModal lang={lang} onClose={() => setShowClearData(false)} />}

      {/* Sticky Bottom Actions — logical positioning so it sits opposite the
          sidebar (which is on the right in Arabic/RTL) and stays visible. */}
      <div className="fixed bottom-4 start-4 end-4 md:start-auto md:end-8 z-[60] flex justify-end items-center gap-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur rounded-2xl border border-gray-150 dark:border-gray-700 shadow-lg p-3">
        {saved && (
          <span className="text-green-600 dark:text-green-400 flex items-center gap-2 text-sm font-semibold animate-fade-in">
            <CheckCircle className="w-5 h-5" />
            <span>{lang === 'ar' ? 'تم الحفظ!' : 'Enregistré!'}</span>
          </span>
        )}
        <button 
          onClick={save}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-xl font-semibold shadow-sm transition-colors border-none"
        >
          {lang === 'ar' ? 'حفظ الكل' : 'Tout enregistrer'}
        </button>
      </div>
    </div>
  );
}
